import type { Control, FieldValues, Path } from 'react-hook-form'
import { StyleSheet, View } from 'react-native'
import { Text } from 'react-native-paper'
import { formatAddress as formatAddressUtil, isAddressBlank } from '@/lib/address'
import type { Addresses as AddressData } from '@/types/pbSchema'
import { TextInput } from '@/ui/form'

export interface AddressEditProps<T extends FieldValues = Record<string, unknown>> {
    control: Control<T>
    namePrefix?: string
    disabled?: boolean
    showTitle?: boolean
}

export interface AddressValidationErrors {
    line1?: string
    city?: string
    stateProvinceCounty?: string
    zipOrPostcode?: string
    country?: string
}

export function AddressEdit<T extends FieldValues = Record<string, unknown>>({
    control,
    namePrefix = 'expand.address',
    disabled = false,
    showTitle = true,
}: AddressEditProps<T>) {
    return (
        <View style={styles.container}>
            {showTitle && (
                <Text variant="headlineSmall" style={styles.title}>
                    Address Information
                </Text>
            )}

            <TextInput
                control={control}
                name={`${namePrefix}.line1` as Path<T>}
                label="Address Line 1"
                placeholder="123 Main Street"
                disabled={disabled}
            />

            <TextInput
                control={control}
                name={`${namePrefix}.line2` as Path<T>}
                label="Address Line 2"
                placeholder="Apartment, suite, etc. (optional)"
                disabled={disabled}
            />

            <View style={styles.row}>
                <View style={styles.cityContainer}>
                    <TextInput
                        control={control}
                        name={`${namePrefix}.city` as Path<T>}
                        label="City"
                        placeholder="New York"
                        disabled={disabled}
                    />
                </View>

                <View style={styles.stateContainer}>
                    <TextInput
                        control={control}
                        name={`${namePrefix}.stateProvinceCounty` as Path<T>}
                        label="State/Province"
                        placeholder="NY"
                        disabled={disabled}
                    />
                </View>
            </View>

            <View style={styles.row}>
                <View style={styles.flex1}>
                    <TextInput
                        control={control}
                        name={`${namePrefix}.zipOrPostcode` as Path<T>}
                        label="ZIP/Postal Code"
                        placeholder="10001"
                        disabled={disabled}
                    />
                </View>

                <View style={styles.flex1}>
                    <TextInput
                        control={control}
                        name={`${namePrefix}.country` as Path<T>}
                        label="Country"
                        placeholder="USA"
                        disabled={disabled}
                    />
                </View>
            </View>
        </View>
    )
}

// Re-export formatAddress from the centralized lib for consistency
export function formatAddress(address: AddressData): string {
    return formatAddressUtil(address)
}

// Check if address has all required fields filled (opposite of isAddressBlank)
export function isAddressComplete(address: AddressData): boolean {
    // Address is complete if it exists and is not blank
    return !isAddressBlank(address)
}

const styles = StyleSheet.create({
    container: {
        gap: 12,
    },
    title: {
        fontWeight: '600',
        color: '#111827',
    },
    row: {
        flexDirection: 'row',
        gap: 12,
    },
    cityContainer: {
        flex: 2,
    },
    stateContainer: {
        flex: 1,
    },
    flex1: {
        flex: 1,
    },
})
